import { Component, Host, Event, h, Prop, Method, Listen, State, Watch, Element, EventEmitter } from '@stencil/core';
import { createPopper, Instance } from '@popperjs/core/lib/popper-lite';
import { Placement } from '@popperjs/core/lib/enums';
import flip from '@popperjs/core/lib/modifiers/flip';
import offset from '@popperjs/core/lib/modifiers/offset';
import arrow from '@popperjs/core/lib/modifiers/arrow';
import preventOverflow from '@popperjs/core/lib/modifiers/preventOverflow';
import { DOMElement } from '../../entities';

@Component({
  tag: 'me-popover',
  styleUrl: 'me-popover.scss',
  shadow: false,
})
export class MePopover {

  @Element() host: HTMLMePopoverElement;

  private static disableBackgroundLinksThreshold = 0.5;
  private static nextPopoverLabelID = 1;
  private readonly popoverLabelID = `me-popover-label-${MePopover.nextPopoverLabelID++}`;

  @Event() mePopoverToggle: EventEmitter<HTMLMePopoverElement>;

  @Prop() popoverTitle: string;
  @Prop() offset = 12;
  @Prop() pagePadding = 16;
  @Prop() dismissable = false;
  @Prop() popperOptions = options => options;
  @Prop({ mutable: true }) popper?: Instance = null;

  @Prop({ mutable: true }) active = false;
  @Watch('active')
  handleActiveChange() {
    this.mePopoverToggle.emit(this.host);
  }

  @Prop() placement: Placement = 'auto';
  @Watch('placement')
  handlePlacementChange() {
    if (!this.popper) { return; }

    this.popper.setOptions(options => ({
      ...options,
      placement: this.placement,
    }));
  }

  @State() private animated = false;

  @Listen('click', { target: 'window' })
  async handleWindowClick(event: PointerEvent) {
    await this.handleToggleClick(event) || await this.handleDismissClick(event);
  }

  @Listen('transitionend')
  async handleTransitionEnd(event) {
    if (event.target !== this.host) { return; }

    if (!this.active) {
      await this.disablePopper();
    }
  }

  disconnectedCallback() {
    this.destroyPopper();
  }

  render() {
    const classList = [
      'me-popover',
      this.active ? 'me-popover--active' : null,
      this.animated ? 'me-popover--animated' : null,
    ].filter(c => !!c).join(' ');

    return (
      <Host class={classList} role="dialog" aria-labelledby={this.popoverLabelID}>
        <div class="me-popover__arrow" data-popper-arrow=""></div>
        <div class="me-popover__header">
          <div id={this.popoverLabelID} class="me-popover__title">
            {this.popoverTitle}
          </div>
          <me-button
            class="me-popover__close-button"
            variant="link"
            onClick={this.close.bind(this)}
          >
            <i class="fa fa-xmark fa-lg" aria-label="Schließen" />
          </me-button>
        </div>
        <div class="me-popover__body">
          <slot></slot>
        </div>
      </Host>
    );
  }

  @Method()
  async toggle(target: DOMElement) {
    this.active
      ? await this.close()
      : await this.open(target);
  }

  @Method()
  async open(target: DOMElement) {
    await this.initPopper(target);
    await this.enablePopper();

    this.animated = true;
    this.active = true;
  }

  @Method()
  async close() {
    this.active = false;

    // `this.disablePopper()` is called by `this.handleTransitionEnd()`
  }

  private async initPopper(target: DOMElement) {
    if (this.popper) { return; }

    this.popper = createPopper(target, this.host, this.popperOptions({
      modifiers: [
        arrow,
        flip,
        offset,
        preventOverflow,
        {
          name: 'offset',
          options: {
            offset: [0, this.offset],
          },
        },
        {
          name: 'preventOverflow',
          options: {
            padding: this.pagePadding,
          },
        },
        {
          name: 'flip',
          options: {
            allowedAutoPlacements: ['top-start', 'top', 'top-end', 'bottom-start', 'bottom', 'bottom-end'],
            fallbackPlacements: [],
          },
        },
      ],
      placement: this.placement,
    }));
  }

  private async disablePopper() {
    await this.enablePopper(false);
  }

  private async enablePopper(enabled = true) {
    if (this.popper) { return; }

    await this.popper.setOptions((options) => ({
      ...options,
      modifiers: [
        ...options.modifiers,
        { name: 'eventListeners', enabled },
      ],
    }));

    await this.popper.update();
  }

  private destroyPopper() {
    if (!this.popper) { return; }

    this.popper.destroy;
    this.popper = null;
  }

  /**
   * Returns whether the event was a toggle click and got handled properly or not
   */
  private async handleToggleClick(event: PointerEvent) {
    if (!(event.target instanceof DOMElement)) { return false; }

    const target = event.target.closest(`[data-me-popover-toggle="${this.host.id}"]`);

    if (!(target instanceof DOMElement)) {
      return false;
    }

    await this.toggle(target);
    return true;
  }

  /**
   * Returns whether the event was a dismiss click and got handled properly or not
   */
  private async handleDismissClick(event: PointerEvent) {
    if (
      !(event.target instanceof DOMElement)
      || !this.dismissable
      || !this.active
      || this.host.contains(event.target)
    ) {
      return false;
    }

    if (this.coverage() >= MePopover.disableBackgroundLinksThreshold) {
      event.preventDefault();
    }

    await this.host.close();
    return true;
  }

  private coverage() {
    const windowArea = window.innerHeight * window.innerWidth;
    const popoverArea = this.host.offsetHeight * this.host.offsetWidth;
    return popoverArea / windowArea;
  }

}
